import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import '../../../styles/ui.css';
import { IconX, IconCheck, IconAlert, IconInfo } from '../icons';

/*
 * dialogs — the app's own confirm / prompt / choose / alert dialogs and a
 * toast stack, replacing every native window.alert/confirm/prompt.
 *
 *   const { confirm, prompt, choose, alert } = useDialog();
 *   if (!(await confirm({ title: 'Delete topic?', message: '…', confirmLabel: 'Delete', danger: true }))) return;
 *   const name = await prompt({ title: 'Rename topic', defaultValue: t.name });   // null = cancelled
 *   const pick = await choose({ title: 'Leave the round?', options: [{ value: 'assess', label: 'See assessment', primary: true }, { value: 'leave', label: 'Leave', danger: true }, { value: 'cancel', label: 'Cancel', ghost: true }] });
 *   const toast = useToast(); toast.success('Saved'); toast.error(e.message);
 *
 * Promise-based so call sites read like the native calls they replace. Mount
 * <DialogProvider> as the first child INSIDE .nano-root (it reads --n-* tokens
 * and the --mx accent). <DialogBridge bind={fn}/> hands the API to code that
 * runs outside a component (e.g. NanoApp's own handlers).
 */
const DialogCtx = createContext(null);
const ToastCtx = createContext(null);
let seq = 0;

export function DialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [toasts, setToasts] = useState([]);

  const open = useCallback((spec) => new Promise((resolve) => {
    setDialog((prev) => { prev?.resolve(prev.kind === 'confirm' ? false : prev.kind === 'prompt' || prev.kind === 'choose' ? null : undefined); return { id: ++seq, ...spec, resolve }; });
  }), []);

  const dialogs = useMemo(() => ({
    confirm: (o) => open({ kind: 'confirm', confirmLabel: 'Confirm', cancelLabel: 'Cancel', danger: false, ...o }),
    prompt: (o) => open({ kind: 'prompt', confirmLabel: 'Save', cancelLabel: 'Cancel', defaultValue: '', placeholder: '', validate: null, ...o }),
    choose: (o) => open({ kind: 'choose', options: [], ...o }),
    alert: (o) => open({ kind: 'alert', confirmLabel: 'OK', ...(typeof o === 'string' ? { message: o } : o) }),
  }), [open]);

  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const push = useCallback((kind, message, { duration } = {}) => {
    const id = ++seq;
    setToasts((t) => [...t.slice(-3), { id, kind, message }]);
    window.setTimeout(() => dismiss(id), duration ?? (kind === 'error' ? 6000 : 3500));
    return id;
  }, [dismiss]);
  const toast = useMemo(() => ({
    success: (m, o) => push('success', m, o),
    info: (m, o) => push('info', m, o),
    error: (m, o) => push('error', m, o),
    dismiss,
  }), [push, dismiss]);

  const close = useCallback((value) => {
    setDialog((d) => { d?.resolve(value); return null; });
  }, []);

  return (
    <DialogCtx.Provider value={dialogs}>
      <ToastCtx.Provider value={toast}>
        {children}
        <div className="ui-layer">
          {dialog && <DialogCard key={dialog.id} dialog={dialog} onClose={close} />}
          <ToastStack toasts={toasts} onDismiss={dismiss} />
        </div>
      </ToastCtx.Provider>
    </DialogCtx.Provider>
  );
}

export const useDialog = () => {
  const c = useContext(DialogCtx);
  if (!c) throw new Error('useDialog must be used within DialogProvider');
  return c;
};
export const useToast = () => {
  const c = useContext(ToastCtx);
  if (!c) throw new Error('useToast must be used within DialogProvider');
  return c;
};

/** Hands { confirm, prompt, choose, alert, toast } to non-component code. */
export const DialogBridge = ({ bind }) => {
  const d = useDialog();
  const t = useToast();
  useEffect(() => { bind({ ...d, toast: t }); }, [d, t, bind]);
  return null;
};

const cancelValue = (kind) => (kind === 'confirm' ? false : kind === 'prompt' || kind === 'choose' ? null : undefined);

function DialogCard({ dialog, onClose }) {
  const { id, kind, title, message, confirmLabel, cancelLabel, danger, defaultValue, placeholder, validate, options } = dialog;
  const cardRef = useRef(null);
  const [val, setVal] = useState(defaultValue || '');
  const err = kind === 'prompt' && validate ? validate(val) : '';
  const cancel = useCallback(() => onClose(cancelValue(kind)), [onClose, kind]);
  const confirm = useCallback(() => {
    if (kind === 'prompt') { if (!val.trim() || err) return; onClose(val.trim()); return; }
    if (kind === 'confirm') { onClose(true); return; }
    onClose(undefined);
  }, [kind, val, err, onClose]);

  useEffect(() => {
    const previous = document.activeElement;
    const card = cardRef.current;
    const focusable = () => Array.from(card.querySelectorAll('button:not(:disabled), input'));
    const first = card.querySelector(kind === 'prompt' ? 'input' : (danger ? '.ui-btn.ghost' : '.ui-btn.primary')) || focusable()[0];
    first?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel(); return; }
      if (e.key === 'Enter' && !e.shiftKey && kind !== 'choose' && !(document.activeElement && document.activeElement.classList.contains('ghost'))) { e.preventDefault(); confirm(); return; }
      if (e.key === 'Tab') {
        const els = focusable();
        if (!els.length) return;
        const i = els.indexOf(document.activeElement);
        if (e.shiftKey && (i <= 0)) { e.preventDefault(); els[els.length - 1].focus(); }
        else if (!e.shiftKey && i === els.length - 1) { e.preventDefault(); els[0].focus(); }
      }
    };
    card.addEventListener('keydown', onKey);
    return () => { card.removeEventListener('keydown', onKey); previous?.focus?.(); };
  }, [kind, danger, cancel, confirm]);

  return (
    <div className="ui-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) cancel(); }}>
      <div className={`ui-dialog${danger ? ' danger' : ''}`} role={kind === 'alert' ? 'alertdialog' : 'dialog'} aria-modal="true" aria-labelledby={`ui-dlg-title-${id}`} ref={cardRef}>
        {title && <h2 id={`ui-dlg-title-${id}`} className="ui-dialog-title">{title}</h2>}
        {message && <p className="ui-dialog-msg">{message}</p>}
        {kind === 'prompt' && (
          <>
            <input className="ui-input" value={val} placeholder={placeholder} maxLength={120} onChange={(e) => setVal(e.target.value)} />
            {err && val && <div className="ui-field-err">{err}</div>}
          </>
        )}
        <div className={`ui-dialog-foot${kind === 'choose' ? ' stack' : ''}`}>
          {kind === 'choose' ? options.map((o) => (
            <button key={String(o.value)} type="button"
              className={`ui-btn${o.primary ? ' primary' : ''}${o.danger ? ' primary danger' : ''}${o.ghost || (!o.primary && !o.danger) ? ' ghost' : ''}`}
              onClick={() => onClose(o.value)}>{o.label}</button>
          )) : (
            <>
              {kind !== 'alert' && <button type="button" className="ui-btn ghost" onClick={cancel}>{cancelLabel}</button>}
              <button type="button" className={`ui-btn primary${danger ? ' danger' : ''}`} disabled={kind === 'prompt' && (!val.trim() || !!err)} onClick={confirm}>{confirmLabel}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ToastStack({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div className="ui-toasts" role="status" aria-live="polite">
      {toasts.map(({ id, kind, message }) => (
        <div key={id} className={`ui-toast ${kind}`}>
          <span className="n-ico">{kind === 'success' ? <IconCheck size={15} /> : kind === 'error' ? <IconAlert size={15} /> : <IconInfo size={15} />}</span>
          <span>{message}</span>
          <button type="button" className="ui-toast-x" aria-label="Dismiss" onClick={() => onDismiss(id)}><IconX size={13} /></button>
        </div>
      ))}
    </div>
  );
}
